//React imports
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

// External libraries

import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import PropTypes from 'prop-types';

// UIComponents
import H3 from 'UIComponents/elements/headings/H3';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';

// MeetingsBase Models and utils
import Branding from 'MeetingsBase/models/Branding';
import { getTextColor } from 'MeetingsBase/utils/color';

// Redux selectors
// @ts-expect-error untyped yet
import { getHasMoreLinkAvailabilityByMonthOffset } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getCustomAvailabilityStartDate, getPushbackTime
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/BookInfoSelectors';
import { useCurrentMonthOffset } from '../../../context/PublicAvailabilityInfoContext';
// Types
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getMonthButtonOpacity(disabled) {
  return disabled ? '0.5' : 1;
}
function DatePickerHeader({
  startOfMonth,
  availableDateOptions,
  setMonthsAhead,
  isTroubleshooting,
  compact,
  branding,
  isLoadingLinkAvailabilityPage,
  hasCustomAvailability,
  renderedMonthLabelRef,
  isPreviewed
}) {
  const hasMoreByOffset = useSelector(getHasMoreLinkAvailabilityByMonthOffset);
  const currentMonthOffset = useCurrentMonthOffset();
  const hasMoreAtCurrentOffset = useMemo(() => {
    return hasMoreByOffset.get(`${currentMonthOffset}`);
  }, [currentMonthOffset, hasMoreByOffset]);
  const customAvailabilityStartDate = useSelector(getCustomAvailabilityStartDate);
  const pushBackTime = useSelector(getPushbackTime);

  // minMonth is determined by the current month when there's no custom availability
  // otherwise it is determined by the custom availableStartTime eturned by the BE
  const isMinMonth = useMemo(() => {
    const customAvailabilityStartTime = customAvailabilityStartDate + pushBackTime;
    const expiredCustomAvailabilityStartTime = customAvailabilityStartDate < Date.now();
    if (!hasCustomAvailability || expiredCustomAvailabilityStartTime) {
      return !isTroubleshooting && I18n.moment().month() === startOfMonth.month() && I18n.moment().year() === startOfMonth.year();
    }
    return !isTroubleshooting && I18n.moment(customAvailabilityStartTime).utc().month() === startOfMonth.month() && I18n.moment(customAvailabilityStartTime).utc().year() === startOfMonth.year();
  }, [customAvailabilityStartDate, hasCustomAvailability, isTroubleshooting, startOfMonth, pushBackTime]);
  const isMaxMonth = useMemo(() => {
    if (isPreviewed) {
      return I18n.moment(availableDateOptions.max()).month() === startOfMonth.month() && I18n.moment(availableDateOptions.max()).year() === startOfMonth.year();
    }
    if (isTroubleshooting || hasMoreAtCurrentOffset) {
      return false;
    }
    return I18n.moment(availableDateOptions.max()).month() === startOfMonth.month() && I18n.moment(availableDateOptions.max()).year() === startOfMonth.year() || !hasMoreAtCurrentOffset;
  }, [isPreviewed, isTroubleshooting, availableDateOptions, startOfMonth, hasMoreAtCurrentOffset]);
  const headerClasses = "date-picker-header " + (compact ? 'm-bottom-4' : 'm-bottom-6');
  const monthBackOpacity = getMonthButtonOpacity(isMinMonth);
  const monthForwardOpacity = getMonthButtonOpacity(isMaxMonth);
  const textColor = getTextColor(branding.primaryColor);
  const currentMonthName = startOfMonth.format('MMMM YYYY');
  const previousMonthName = startOfMonth.clone().add(-1, 'months').format('MMMM');
  const nextMonthName = startOfMonth.clone().add(1, 'months').format('MMMM');
  const getPrevMonth = useCallback(() => {
    // @ts-expect-error TODO
    setMonthsAhead(_monthsAhead => _monthsAhead - 1);
  }, [setMonthsAhead]);
  const getNextMonth = useCallback(() => {
    // @ts-expect-error TODO
    setMonthsAhead(_monthsAhead => _monthsAhead + 1);
  }, [setMonthsAhead]);
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    justify: "around",
    className: headerClasses,
    children: [/*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: previousMonthName
        }
      }),
      disabled: !isMinMonth,
      placement: "right",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "transparent",
        "data-selenium-test": "previous-month-button",
        onClick: getPrevMonth,
        disabled: isMinMonth,
        "aria-label": I18n.text('prebook.datePicker.previousMonth', {
          month: previousMonthName
        }),
        "data-test-id": "date-picker-header-prev-btn",
        children: /*#__PURE__*/_jsx(UIIcon, {
          className: "month-button-icon",
          name: "left",
          color: textColor,
          style: {
            opacity: monthBackOpacity
          }
        })
      })
    }), /*#__PURE__*/_jsx(H3, {
      "aria-label": currentMonthName,
      "data-selenium-test": "month-and-year",
      "aria-level": 2,
      className: "m-bottom-0",
      style: {
        minWidth: 80,
        textAlign: 'center'
      },
      children: /*#__PURE__*/_jsx("span", {
        ref: renderedMonthLabelRef,
        "data-test-id": "current-month-label",
        children: currentMonthName
      })
    }), /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: nextMonthName
        }
      }),
      disabled: !isMaxMonth || isLoadingLinkAvailabilityPage,
      placement: "left",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "transparent",
        onClick: getNextMonth,
        disabled: isMaxMonth || isLoadingLinkAvailabilityPage,
        "aria-label": I18n.text('prebook.datePicker.nextMonth', {
          month: nextMonthName
        }),
        "data-selenium-test": "next-month-button",
        "data-test-id": "date-picker-header-next-btn",
        children: /*#__PURE__*/_jsx(UIIcon, {
          className: "month-button-icon",
          name: "right",
          color: textColor,
          style: {
            opacity: monthForwardOpacity
          }
        })
      })
    })]
  });
}
DatePickerHeader.propTypes = {
  startOfMonth: PropTypes.object.isRequired,
  availableDateOptions: PropTypes.any,
  setMonthsAhead: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  isTroubleshooting: PropTypes.bool,
  branding: PropTypes.instanceOf(Branding).isRequired,
  isLoadingLinkAvailabilityPage: PropTypes.bool
};
export default DatePickerHeader;